import { Component, computed, input, output, signal, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import {
  MedicationDto,
  ProblemListDto,
  AllergyDto,
  DiagnosisDto,
  AcceptReferralClinicalData
} from '@xmedical/core';

export interface ClinicalDataSelections {
  selectedMedicationIds: Set<number>;
  selectedProblemIds: Set<number>;
  selectedAllergyIds: Set<number>;
  selectedDiagnosisCodes: Set<string>;
}

@Component({
  selector: 'xm-clinical-data-selection',
  templateUrl: './clinical-data-selection.component.html',
  styleUrls: ['./clinical-data-selection.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule
  ]
})
export class ClinicalDataSelectionComponent implements OnInit, OnChanges {
  // Inputs
  readonly medications = input<MedicationDto[]>([]);
  readonly problems = input<ProblemListDto[]>([]);
  readonly allergies = input<AllergyDto[]>([]);
  readonly diagnosis = input<DiagnosisDto[]>([]);
  readonly selectAllByDefault = input(true);

  // Output emitted whenever selections change
  readonly selectionsChange = output<ClinicalDataSelections>();

  // Internal selection state
  readonly selectedMedications = signal<Set<number>>(new Set());
  readonly selectedProblems = signal<Set<number>>(new Set());
  readonly selectedAllergies = signal<Set<number>>(new Set());
  readonly selectedDiagnosis = signal<Set<string>>(new Set());

  readonly hasClinicalData = computed(() =>
    this.medications().length > 0 ||
    this.problems().length > 0 ||
    this.allergies().length > 0 ||
    this.diagnosis().length > 0
  );

  ngOnInit(): void {
    if (this.selectAllByDefault()) {
      this.initializeSelections();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['medications'] || changes['problems'] || changes['allergies'] || changes['diagnosis']) {
      if (this.selectAllByDefault()) {
        this.initializeSelections();
      }
    }
  }

  // Selection checks
  isMedicationSelected(id: number): boolean {
    return this.selectedMedications().has(id);
  }

  isProblemSelected(id: number): boolean {
    return this.selectedProblems().has(id);
  }

  isAllergySelected(id: number): boolean {
    return this.selectedAllergies().has(id);
  }

  isDiagnosisSelected(code: string): boolean {
    return this.selectedDiagnosis().has(code);
  }

  // Individual toggles
  toggleMedication(id: number, selected: boolean): void {
    this.selectedMedications.update(set => {
      const newSet = new Set(set);
      selected ? newSet.add(id) : newSet.delete(id);
      return newSet;
    });
    this.emitSelections();
  }

  toggleProblem(id: number, selected: boolean): void {
    this.selectedProblems.update(set => {
      const newSet = new Set(set);
      selected ? newSet.add(id) : newSet.delete(id);
      return newSet;
    });
    this.emitSelections();
  }

  toggleAllergy(id: number, selected: boolean): void {
    this.selectedAllergies.update(set => {
      const newSet = new Set(set);
      selected ? newSet.add(id) : newSet.delete(id);
      return newSet;
    });
    this.emitSelections();
  }

  toggleDiagnosis(code: string, selected: boolean): void {
    this.selectedDiagnosis.update(set => {
      const newSet = new Set(set);
      selected ? newSet.add(code) : newSet.delete(code);
      return newSet;
    });
    this.emitSelections();
  }

  // Bulk toggles
  toggleAllMedications(selectAll: boolean): void {
    if (selectAll) {
      this.selectedMedications.set(new Set(this.medications().map(m => m.id)));
    } else {
      this.selectedMedications.set(new Set());
    }
    this.emitSelections();
  }

  toggleAllProblems(selectAll: boolean): void {
    if (selectAll) {
      this.selectedProblems.set(new Set(this.problems().map(p => p.id)));
    } else {
      this.selectedProblems.set(new Set());
    }
    this.emitSelections();
  }

  toggleAllAllergies(selectAll: boolean): void {
    if (selectAll) {
      this.selectedAllergies.set(new Set(this.allergies().map(a => a.id)));
    } else {
      this.selectedAllergies.set(new Set());
    }
    this.emitSelections();
  }

  toggleAllDiagnosis(selectAll: boolean): void {
    if (selectAll) {
      this.selectedDiagnosis.set(new Set(this.diagnosis().map(d => d.icD10Code)));
    } else {
      this.selectedDiagnosis.set(new Set());
    }
    this.emitSelections();
  }

  /** Build the AcceptReferralClinicalData from current selections */
  buildClinicalData(): AcceptReferralClinicalData {
    return {
      addMedicationIds: Array.from(this.selectedMedications()),
      addProblemIds: Array.from(this.selectedProblems()),
      addAllergyIds: Array.from(this.selectedAllergies()),
      addDiagnosisCodes: Array.from(this.selectedDiagnosis()),
      removeMedicationIds: [],
      removeProblemIds: [],
      removeAllergyIds: [],
      removeDiagnosisCodes: []
    };
  }

  private initializeSelections(): void {
    this.selectedMedications.set(new Set(this.medications().map(m => m.id)));
    this.selectedProblems.set(new Set(this.problems().map(p => p.id)));
    this.selectedAllergies.set(new Set(this.allergies().map(a => a.id)));
    this.selectedDiagnosis.set(new Set(this.diagnosis().map(d => d.icD10Code)));
    this.emitSelections();
  }

  private emitSelections(): void {
    this.selectionsChange.emit({
      selectedMedicationIds: this.selectedMedications(),
      selectedProblemIds: this.selectedProblems(),
      selectedAllergyIds: this.selectedAllergies(),
      selectedDiagnosisCodes: this.selectedDiagnosis()
    });
  }
}
